import { Clock, Eye, MapPin, Truck } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Pagination from "../../components/ui/dashboard/Pagination";
import ThreeDotsMenu from "../../components/ui/ThreeDotsMenu";
import {
  Dispatch,
  DispatchSearchRequest,
  DispatchSearchResponse,
} from "./@types";
import { useSearchDispatchesMutation } from "./dispatchApiSlice";

const OnGoingTrips: React.FC = () => {
  const [currentPage, setCurrentPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const navigate = useNavigate();

  const [selectedDispatch, setSelectedDispatch] = useState<Dispatch | null>(
    null
  );

  // RTK Query mutation
  const [getDispatchHistory, { data: dispatchHistoryData, isLoading }] =
    useSearchDispatchesMutation();

  const fetchDispatchHistory = useCallback(async () => {
    const requestBody: DispatchSearchRequest = {
      pageNumber: currentPage,
      pageSize: pageSize,
      tripStatus: "InTransit",
    };

    try {
      await getDispatchHistory(requestBody).unwrap();
    } catch (err) {
      console.error("Failed to fetch dispatch history:", err);
    }
  }, [currentPage, pageSize, getDispatchHistory]);

  useEffect(() => {
    fetchDispatchHistory();
  }, [currentPage, pageSize, fetchDispatchHistory]);

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handlePageSizeChange = (size: number) => {
    setPageSize(size);
    setCurrentPage(0);
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case "in-progress":
        return "bg-blue-100 text-blue-700 border-blue-200";
      case "scheduled":
        return "bg-yellow-100 text-yellow-700 border-yellow-200";
      case "completed":
        return "bg-green-100 text-green-700 border-green-200";
      default:
        return "bg-gray-100 text-gray-700 border-gray-200";
    }
  };

  const response = dispatchHistoryData?.data as
    | DispatchSearchResponse
    | undefined;

  return (
    <div className="max-h-[90vh] py-6 w-full bg-[#F9F9F9] p-4">
      <div className="w-full mx-auto space-y-6">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left Panel - Dispatch List */}
          <div className="col-span-1 space-y-4">
            <div className="flex flex-col  bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
              <div className="flex items-center justify-between">
                <div className="flex p-4 items-center gap-2">
                  <div className="bg-green-600 h-3 w-3 rounded-full"></div>
                  <h2 className="font-semibold text-lg">
                    Ongoing trips {response?.totalCount || 0}
                  </h2>
                </div>

                <div className="">
                  <ThreeDotsMenu
                    actions={[
                      {
                        label: "All dispatch",
                        onClick: () =>
                          navigate(
                            `/admin/dispatch-history/${response?.data[0]?.vehicleId}`
                          ),
                        icon: <Eye className="w-4 h-4" />,
                      },
                    ]}
                  />
                </div>
              </div>

              <div className="max-h-[77vh] overflow-y-auto p-4">
                {isLoading ? (
                  <div className="flex items-center justify-center p-8">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                  </div>
                ) : response && response.data.length > 0 ? (
                  <div className="flex flex-col space-y-4">
                    {response.data.map((dispatch) => (
                      <div
                        key={dispatch.id}
                        onClick={() => setSelectedDispatch(dispatch)}
                        className={`p-4 cursor-pointer transition-all hover:bg-gray-50 border-2 rounded-lg space-y-2 ${
                          selectedDispatch?.id === dispatch.id
                            ? "border-primary"
                            : "border-gray-200"
                        }`}
                      >
                        <div className="flex items-start justify-between">
                          <div className="bg-primary/10 text-primary px-2 py-0.5 rounded-lg">
                            <p className="text-primary text-[14px]">
                              {dispatch.vehicleModel}
                            </p>
                          </div>
                          <span
                            className={`text-xs font-semibold text-secondary`}
                          >
                            {dispatch.approximateDistance} km
                          </span>
                        </div>

                        <div className="">
                          <div className="flex items-center gap-2 text-hColor">
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              width="24"
                              height="24"
                              viewBox="0 0 24 24"
                              className="w-4 h-4 text-gray-500"
                            >
                              <path
                                fill="currentColor"
                                d="M22 18.714c0 .888-.371 1.729-1.014 2.34c-.64.611-1.5.946-2.386.946H5.4a3.46 3.46 0 0 1-2.386-.945A3.23 3.23 0 0 1 2 18.714c0-1.797.75-3.51 2.067-4.765A7.16 7.16 0 0 1 9 12h6c1.841 0 3.616.696 4.933 1.95S22 16.916 22 18.713M17 6A5 5 0 1 1 7 6a5 5 0 0 1 10 0"
                              />
                            </svg>
                            <p className="font-semibold text-[12px]">
                              {dispatch.driverName}
                            </p>
                          </div>
                        </div>

                        <div className="space-y-2 text-sm">
                          <div className="flex items-start gap-2">
                            <MapPin className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
                            <div className="flex-1 min-w-0">
                              <p className="font-medium text-gray-900 truncate">
                                {dispatch.fromLocation}
                              </p>
                            </div>
                          </div>
                          <div className="flex items-start gap-2">
                            <MapPin className="w-4 h-4 text-red-600 mt-0.5 flex-shrink-0" />
                            <div className="flex-1 min-w-0">
                              <p className="font-medium text-gray-900 truncate">
                                {dispatch.toLocation}
                              </p>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="flex flex-col items-center justify-center p-12">
                    <Truck className="w-16 h-16 text-gray-300 mb-3" />
                    <p className="text-gray-500 font-medium">
                      No ongoing trips
                    </p>
                    <p className="text-gray-400 text-sm">
                      Active dispatches will appear here
                    </p>
                  </div>
                )}
              </div>
              {response && response.data.length > 0 && (
                <div className="left-32 right-0">
                  <div>
                    <Pagination
                      currentPage={response.currentPage}
                      totalPages={response.totalPages}
                      pageSize={response.pageSize}
                      totalCount={response.totalCount}
                      hasNextPage={response.hasNextPage}
                      hasPreviousPage={response.hasPreviousPage}
                      onPageChange={handlePageChange}
                      onPageSizeChange={handlePageSizeChange}
                      showPageSizeSelector={false}
                    />
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Right Panel - Details & Map */}
          <div className="lg:col-span-2 space-y-6">
            {selectedDispatch ? (
              <>
                {/* Map Placeholder */}
                <div className="h-[88vh] bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden p-5">
                  <div className="relative h-full bg-gradient-to-br from-primary/20 to-secondary/20 flex items-center justify-center rounded-xl">
                    <div className="absolute inset-0 opacity-10">
                      <svg
                        className="w-full h-full"
                        viewBox="0 0 100 100"
                        preserveAspectRatio="none"
                      >
                        <path
                          d="M0,50 Q25,30 50,50 T100,50"
                          stroke="currentColor"
                          fill="none"
                          strokeWidth="0.5"
                          className="text-primary"
                        />
                        <path
                          d="M0,60 Q25,40 50,60 T100,60"
                          stroke="currentColor"
                          fill="none"
                          strokeWidth="0.5"
                          className="text-secondary"
                        />
                      </svg>
                    </div>
                    <div className="text-center z-10 bg-white rounded-xl p-8 shadow-lg border border-gray-200 max-w-md">
                      <MapPin className="w-16 h-16 text-primary mx-auto mb-4" />
                      <h4 className="text-xl font-bold text-gray-900 mb-2">
                        Map Integration Coming Soon
                      </h4>
                      <p className="text-gray-600 mb-4">
                        Live tracking map for trip{" "}
                        <span className="font-semibold text-primary">
                          {selectedDispatch.id}
                        </span>{" "}
                        is currently under development
                      </p>
                      <div className="inline-flex items-center gap-2 px-4 py-2 bg-gray-100 text-primary rounded-lg text-sm font-medium">
                        <Clock className="w-4 h-4" />
                        <span>Feature in progress</span>
                      </div>
                    </div>
                  </div>
                </div>
              </>
            ) : (
              <div className="h-[88vh] bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden p-5">
                <div className="relative h-full bg-gradient-to-br from-primary/20 to-secondary/20 flex items-center justify-center rounded-xl">
                  <div className="absolute inset-0 opacity-10">
                    <svg
                      className="w-full h-full"
                      viewBox="0 0 100 100"
                      preserveAspectRatio="none"
                    >
                      <path
                        d="M0,50 Q25,30 50,50 T100,50"
                        stroke="currentColor"
                        fill="none"
                        strokeWidth="0.5"
                        className="text-primary"
                      />
                      <path
                        d="M0,60 Q25,40 50,60 T100,60"
                        stroke="currentColor"
                        fill="none"
                        strokeWidth="0.5"
                        className="text-secondary"
                      />
                    </svg>
                  </div>
                  <div className="text-center z-10 bg-white rounded-xl p-8 shadow-lg border border-gray-200 max-w-md">
                    <MapPin className="w-16 h-16 text-primary mx-auto mb-4" />
                    <h4 className="text-xl font-bold text-gray-900 mb-2">
                      Map Integration Coming Soon
                    </h4>
                    <Truck className="w-16 h-16 text-gray-300 mx-auto mb-3" />
                    <p className="text-gray-500 font-medium">
                      Select a dispatch to view details
                    </p>
                    <div className="inline-flex items-center gap-2 px-4 py-2 bg-gray-100 text-primary rounded-lg text-sm font-medium">
                      <Clock className="w-4 h-4" />
                      <span>Feature in progress</span>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default OnGoingTrips;
